import { Controller } from "@hotwired/stimulus";

type StreamEvent = CustomEvent<{ render: (stream: HTMLElement) => void | Promise<void> }>;
type Pair = { incoming: HTMLElement; current?: HTMLElement };

// The conversation as one chronological list. Every request groups the steps
// Casey took for it (tool results, and for a super admin the model's
// narration) into one collapsed disclosure; the status row docks into that
// disclosure's summary while the turn runs, and the summary reads
// "Worked for 1m 42s" once it is over.
export default class extends Controller<HTMLElement> {
  static targets = ["entry", "list", "activityTemplate", "status", "statusHome"];
  static values = {
    worked: { type: String, default: "Worked for %{duration}" },
    explainMore: { type: String, default: "Explain more" },
    explainLess: { type: String, default: "Show less" },
  };
  declare readonly entryTargets: HTMLElement[];
  declare readonly listTarget: HTMLElement;
  declare readonly activityTemplateTarget: HTMLTemplateElement;
  declare readonly statusTarget: HTMLElement;
  declare readonly hasStatusTarget: boolean;
  declare readonly statusHomeTarget: HTMLElement;
  declare readonly hasStatusHomeTarget: boolean;
  declare readonly workedValue: string;
  declare readonly explainMoreValue: string;
  declare readonly explainLessValue: string;
  private scheduled = false;
  private submittedControl?: HTMLElement;

  connect(): void {
    this.schedule();
  }

  entryTargetConnected(): void {
    this.schedule();
  }

  entryTargetDisconnected(): void {
    this.schedule();
  }

  statusTargetConnected(): void {
    this.schedule();
  }

  submissionStarted(event: CustomEvent<{ formSubmission: { submitter?: HTMLElement } }>): void {
    const submitter = event.detail.formSubmission.submitter;
    if (submitter && this.element.contains(submitter)) this.submittedControl = submitter;
  }

  submissionFinished(event: CustomEvent<{ error?: Error; formSubmission: { submitter?: HTMLElement } }>): void {
    if (event.detail.error && event.detail.formSubmission.submitter === this.submittedControl) {
      this.submittedControl = undefined;
    }
  }

  toggleExplain(event: Event): void {
    const group = (event.currentTarget as HTMLElement).closest<HTMLDetailsElement>("[data-assistant-activity]");
    if (!group) return;
    group.toggleAttribute("data-explain");
    this.explain(group);
  }

  beforeStreamRender(event: StreamEvent): void {
    const stream = event.target as HTMLElement;
    const content = stream.querySelector<HTMLTemplateElement>("template")?.content;
    const incomings = content ? Array.from(content.querySelectorAll<HTMLElement>('[data-assistant-timeline-target~="entry"]')) : [];
    if (!incomings.length || this.element.id !== `assistant_messages_${incomings[0].dataset.timelineChat}`) return;

    const render = event.detail.render;
    event.detail.render = async (element) => {
      if (!this.element.isConnected) return;
      const pairs: Pair[] = incomings.map((incoming) => ({ incoming, current: this.entryTargets.find((entry) => entry.id === incoming.id) }));
      // A message can render several entries (a step row and a card); drop the stale ones.
      const fresh = pairs.filter(({ current, incoming }) => {
        const stale = !!current && this.stale(current, incoming);
        if (stale) incoming.remove();
        return !stale;
      });
      if (!fresh.length) return;

      const focused = document.activeElement === document.body
        ? this.submittedControl || document.activeElement : document.activeElement;
      fresh.forEach(({ current, incoming }) => {
        if (!current) return;
        if (current.contains(this.submittedControl || null)) this.submittedControl = undefined;
        if (current.dataset.timelineState === incoming.dataset.timelineState) {
          const expanded = Array.from(current.querySelectorAll("details")).map((details) => details.open);
          incoming.querySelectorAll("details").forEach((details, index) => { details.open = expanded[index] || false; });
        }
        // A replacement can arrive before its append, or target an entry inside
        // the activity disclosure. Upsert into one list, then restore chronology.
        current.remove();
      });
      element.setAttribute("action", "append");
      element.setAttribute("target", this.element.id);
      await render(element);
      this.layout();
      const { current, incoming } = fresh[0];
      if (document.activeElement === document.body || document.activeElement === focused) {
        const replacement = this.entryTargets.find((entry) => entry.id === incoming.id);
        if (replacement) this.focusTarget(current, replacement, focused)?.focus({ preventScroll: true });
      }
    };
  }

  private focusTarget(current: HTMLElement | undefined, incoming: HTMLElement, focused: Element | null): HTMLElement | null {
    if (!current?.contains(focused) || !(focused instanceof HTMLElement)) return null;

    const summary = incoming.querySelector<HTMLElement>("[data-assistant-receipt-summary]");
    if (current.dataset.timelineState !== incoming.dataset.timelineState) return summary;

    if (focused.id) {
      return Array.from(incoming.querySelectorAll<HTMLElement>("[id]"))
        .find((element) => element.id === focused.id) || summary;
    }
    if (focused instanceof HTMLButtonElement && focused.form) {
      return Array.from(incoming.querySelectorAll<HTMLFormElement>("form"))
        .find((form) => form.action === focused.form!.action)?.querySelector("button") || summary;
    }
    if (focused instanceof HTMLAnchorElement) {
      return Array.from(incoming.querySelectorAll<HTMLAnchorElement>("a[href]"))
        .find((link) => link.href === focused.href) || summary;
    }
    return summary;
  }

  private stale(current: HTMLElement, incoming: HTMLElement): boolean {
    const version = Number(current.dataset.timelineVersion) - Number(incoming.dataset.timelineVersion);
    if (version !== 0) return version > 0;

    const sequence = (entry: HTMLElement) => entry.dataset.timelineStreamSequence === "complete"
      ? Infinity : Number(entry.dataset.timelineStreamSequence || 0);
    return sequence(current) > sequence(incoming);
  }

  private schedule(): void {
    if (this.scheduled || document.documentElement.hasAttribute("data-turbo-preview")) return;
    this.scheduled = true;
    queueMicrotask(() => {
      this.scheduled = false;
      if (this.element.isConnected) this.layout();
    });
  }

  private layout(): void {
    const entries = [...this.entryTargets].sort((left, right) =>
      Number(left.dataset.timelineTime) - Number(right.dataset.timelineTime) ||
      Number(left.dataset.timelineType === "proposal") - Number(right.dataset.timelineType === "proposal") ||
      Number(left.dataset.timelineSequence) - Number(right.dataset.timelineSequence));
    if (entries.length) this.listTarget.querySelector("#assistant_empty")?.remove();
    // An answer that embeds a tool card (data-assistant-embeds) supersedes the card's own entry.
    const embedded = new Set<string>();
    entries.forEach((entry) => entry.querySelectorAll<HTMLElement>("[data-assistant-embeds]")
      .forEach((card) => embedded.add(card.dataset.assistantEmbeds!)));
    entries.forEach((entry) => {
      if (entry.dataset.timelineKind === "tool") entry.classList.toggle("hidden", embedded.has(entry.id));
    });
    const groups = new Map<string, HTMLDetailsElement>();
    this.listTarget.querySelectorAll<HTMLDetailsElement>("[data-assistant-activity]")
      .forEach((group) => groups.set(group.dataset.assistantActivity!, group));
    const activity = new Map<HTMLDetailsElement, HTMLElement[]>();
    const spans = new Map<string, { start: number; end: number }>();
    const visible: HTMLElement[] = [];
    let proposals: HTMLElement[] = [];
    let request = "start";

    entries.forEach((entry) => {
      const kind = entry.dataset.timelineKind;
      const time = Number(entry.dataset.timelineTime);
      if (kind === "user" || entry.dataset.assistantMessageOrigin === "action") {
        visible.push(...proposals);
        proposals = [];
        request = entry.id;
      }
      const span = spans.get(request) || { start: time, end: time };
      span.end = Math.max(span.end, time);
      spans.set(request, span);
      // Review the complete answer before reaching its approval controls.
      if (kind === "proposal") {
        proposals.push(entry);
        return;
      }
      if (kind !== "activity" && kind !== "narration") {
        visible.push(entry);
        return;
      }
      let group = groups.get(request);
      if (!group) {
        group = this.activityTemplateTarget.content.firstElementChild!.cloneNode(true) as HTMLDetailsElement;
        group.dataset.assistantActivity = request;
        group.querySelector("[data-assistant-activity-content]")?.addEventListener("scroll", (event) => this.fade(event.target as HTMLElement));
        groups.set(request, group);
      }
      if (!activity.has(group)) {
        activity.set(group, []);
        visible.push(group);
      }
      activity.get(group)!.push(entry);
    });
    visible.push(...proposals);

    activity.forEach((items, group) => {
      const content = group.querySelector<HTMLElement>("[data-assistant-activity-content]")!;
      this.order(content, items);
      this.explain(group);
      const span = spans.get(group.dataset.assistantActivity!);
      this.title(group, span ? (span.end - span.start) / 1_000_000 : 0);
      this.fade(content);
    });
    groups.forEach((group) => { if (!activity.has(group)) group.remove(); });
    this.order(this.listTarget, visible);
    this.placeStatus(groups.get(request));
  }

  private order(container: HTMLElement, entries: HTMLElement[]): void {
    let next = container.firstElementChild;
    entries.forEach((entry) => {
      if (entry === next) next = next.nextElementSibling;
      else container.insertBefore(entry, next);
    });
  }

  // Narration entries and the call lines under each row show only while the
  // group is in Explain more; every other activity entry is always shown.
  private explain(group: HTMLDetailsElement): void {
    const on = group.hasAttribute("data-explain");
    group.querySelectorAll<HTMLElement>('[data-assistant-timeline-target~="entry"]').forEach((entry) => {
      entry.classList.toggle("hidden", entry.dataset.timelineKind === "narration" && !on);
    });
    group.querySelectorAll<HTMLElement>("[data-assistant-explain]").forEach((line) => { line.hidden = !on; });
    const toggle = group.querySelector<HTMLElement>("[data-assistant-activity-explain]");
    if (toggle) toggle.textContent = on ? this.explainLessValue : this.explainMoreValue;
    group.querySelector<HTMLElement>("[data-assistant-activity-transcript]")?.classList.toggle("hidden", !on);
    group.querySelector<HTMLElement>("[data-assistant-activity-content]")?.classList.toggle("max-h-[300px]", !on);
  }

  private title(group: HTMLDetailsElement, seconds: number): void {
    const title = group.querySelector<HTMLElement>("[data-assistant-activity-title]");
    if (title) title.textContent = this.workedValue.replace("%{duration}", this.duration(seconds));
  }

  private duration(seconds: number): string {
    const whole = Math.max(0, Math.round(seconds));
    const hours = Math.floor(whole / 3600), minutes = Math.floor((whole % 3600) / 60), rest = whole % 60;
    if (hours) return `${hours}h ${minutes}m`;
    if (minutes) return `${minutes}m ${rest}s`;
    return `${rest}s`;
  }

  // The status row is the header of the request that is running; at rest it
  // returns home under the list, where paused and failed turns are handled.
  private placeStatus(group?: HTMLDetailsElement): void {
    if (!this.hasStatusTarget) return;
    const status = this.statusTarget;
    const busy = ["queued", "working"].includes(status.dataset.state || "");
    const slot = busy ? group?.querySelector<HTMLElement>("[data-assistant-activity-status]") : undefined;
    this.listTarget.querySelectorAll<HTMLElement>("[data-assistant-activity-title]").forEach((title) => {
      title.hidden = slot ? title.closest("[data-assistant-activity]") === group : false;
    });
    if (slot) {
      if (status.parentElement !== slot) slot.append(status);
    } else if (this.hasStatusHomeTarget && status.parentElement !== this.statusHomeTarget) {
      this.statusHomeTarget.append(status);
    }
  }

  // Fade the clipped edges of a scrolled list, and only those.
  private fade(content: HTMLElement): void {
    const top = content.scrollTop > 4;
    const bottom = content.scrollTop + content.clientHeight < content.scrollHeight - 4;
    const stops = [top ? "transparent, black 26px" : "black", bottom ? "black calc(100% - 26px), transparent" : "black"];
    const mask = top || bottom ? `linear-gradient(${stops.join(", ")})` : "";
    content.style.maskImage = mask;
    content.style.webkitMaskImage = mask;
  }
}
